import { useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { RED, RED_LIGHT, GREEN_LIGHT } from "assets/theme/brandColors";

const PAGE_SIZE = 3;

function SchedulesCard({ schedules, onDelete }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState(null);

  // First incomplete schedule is the primary one
  const primaryIndex = schedules.findIndex(
    (s) => s.total_count === 0 || s.completed_count < s.total_count
  );

  const totalPages = Math.ceil(schedules.length / PAGE_SIZE);
  const pageItems = schedules.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Adjust primary index to the current page slice
  const primaryPageIndex = primaryIndex - (page - 1) * PAGE_SIZE;

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="medium" mb={2}>
          Your Schedules
        </MDTypography>

        {pageItems.map((schedule, index) => {
          const globalIndex = (page - 1) * PAGE_SIZE + index;
          const isCompleted =
            schedule.total_count > 0 && schedule.completed_count >= schedule.total_count;
          const isPrimary = globalIndex === primaryIndex;
          const progressLabel =
            schedule.total_count > 0
              ? `${schedule.completed_count} of ${schedule.total_count} recipes`
              : "No recipes yet";

          return (
            <MDBox
              key={schedule.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                py: 1.5,
                px: isPrimary ? 1.5 : 0,
                mb: isPrimary ? 1 : 0,
                borderRadius: isPrimary ? "10px" : 0,
                background: isPrimary ? "rgba(160,28,39,0.05)" : "transparent",
                borderBottom: !isPrimary ? "1px solid #f0f0f0" : "none",
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <MDBox>
                <MDTypography
                  variant="button"
                  fontWeight={isPrimary ? "bold" : "regular"}
                  color="text"
                  display="block"
                >
                  {schedule.name}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#555" }}
                >
                  {progressLabel}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" gap={1} alignItems="center">
                <Chip
                  label={isCompleted ? "Done ✓" : "Active"}
                  size="small"
                  sx={
                    isCompleted
                      ? {
                          bgcolor: GREEN_LIGHT,
                          color: "#2e7d32",
                          fontWeight: 700,
                          fontSize: "10px",
                        }
                      : { bgcolor: RED_LIGHT, color: RED, fontWeight: 700, fontSize: "10px" }
                  }
                />

                <MDButton
                  variant={isPrimary ? "contained" : "outlined"}
                  color={isPrimary ? "error" : "dark"}
                  size="small"
                  onClick={() =>
                    navigate(`/module/${schedule.id}`, { state: { from: "Dashboard" } })
                  }
                  sx={
                    isPrimary
                      ? {
                          bgcolor: RED,
                          "&:hover": { bgcolor: "#7d1520" },
                          whiteSpace: "nowrap",
                        }
                      : { whiteSpace: "nowrap" }
                  }
                >
                  {isPrimary ? "Continue →" : isCompleted ? "View" : "Resume"}
                </MDButton>

                {onDelete && (
                  <IconButton
                    size="small"
                    onClick={() => setConfirmId(schedule.id)}
                    sx={{ color: "#ccc", "&:hover": { color: RED } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </MDBox>
            </MDBox>
          );
        })}

        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={2}
          flexWrap="wrap"
          gap={1}
        >
          <MDButton
            variant="outlined"
            size="small"
            onClick={() => navigate("/schedule-builder")}
            sx={{ color: RED, borderColor: RED, "&:hover": { borderColor: "#7d1520" } }}
          >
            + New Schedule
          </MDButton>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              size="small"
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: RED,
                  color: "#fff",
                },
              }}
            />
          )}
        </MDBox>
      </MDBox>
      <Dialog open={!!confirmId} onClose={() => setConfirmId(null)}>
        <DialogTitle>Delete schedule?</DialogTitle>
        <DialogContent>This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => {
              onDelete(confirmId);
              setConfirmId(null);
            }}
            sx={{ color: RED }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

SchedulesCard.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      completed_count: PropTypes.number,
      total_count: PropTypes.number,
    })
  ).isRequired,
  onDelete: PropTypes.func,
};

export default SchedulesCard;
